import {mapFields} from 'vuex-map-fields';
import {mapGetters, mapActions} from 'vuex';
import {FilesService} from '@/services/files';
import {MedicineService} from '@/services/medicine';
import {isUploadableFile} from '@/utils/files';
import ToastMixin from '@/mixins/toast-mixin';
import {
  GETTER_PRESCRIPTION_HAS_VALID_COUPON,
  GETTER_PHARMACY_ORDER_SUBMISSION_BODY,
  ACTION_SET_MEDICINE_STATE,
  ACTION_RESET_PHARMACY_ORDER,
} from '@/store/types';
import Routes from '@/router/routes';

export default {
  mixins: [
    ToastMixin,
  ],
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      orderSubmissionBody: GETTER_PHARMACY_ORDER_SUBMISSION_BODY,
      hasValidPrescription: GETTER_PRESCRIPTION_HAS_VALID_COUPON,
    }),
    ...mapFields({
      attachedIds: 'attachedIds',
      uploadedIds: 'uploadedIds',
      attachedPrescriptions: 'attachedPrescriptions',
      uploadedPrescriptions: 'uploadedPrescriptions',
    }),
  },
  methods: {
    ...mapActions({
      setMedicineState: ACTION_SET_MEDICINE_STATE,
      resetPharmacyOrder: ACTION_RESET_PHARMACY_ORDER,
    }),
    async submitOrder() {
      this.loading = true;

      try {
        await this.uploadPrescriptions();
        await this.uploadIds();
        const submissionEndpoint = this.hasValidPrescription ? MedicineService.submitCoupon : MedicineService.submitOrder;
        const response = await submissionEndpoint(this.orderSubmissionBody);
        this.loading = false;
        this.postSubmissionFunction(response);
      } catch (error) {
        this.loading = false;
        this.showToast({
          text: typeof error === typeof 'string' ? error : 'Service is currently unavailable right now. Please try again later.',
          theme: 'danger',
        });
      }
    },
    async submitCoupon() {
      this.loading = true;

      try {
        await this.uploadPrescriptions();
        await this.uploadIds();
        const response = await MedicineService.submitCoupon(this.orderSubmissionBody);
        this.loading = false;
        this.postSubmissionFunction(response);
      } catch (error) {
        this.loading = false;
        this.showToast({
          text: typeof error === typeof 'string' ? error : 'Availing coupon failed.',
          theme: 'danger',
        });
      }
    },
    async uploadPrescriptions() {
      const customPrescriptions = this.attachedPrescriptions.filter(({uploadid}) => !uploadid);

      /**
       * an attached file is only a handle - the OS can reclaim the bytes between
       * picking and submitting, which is a long window here since the upload only
       * happens on submission. reject before building the requests: the response
       * mapping below is positional, so skipping an entry would shift every
       * uploadid onto the wrong prescription.
       */
      if (customPrescriptions.some(({file}) => !isUploadableFile(file))) {
        return Promise.reject('One of your attached prescriptions is no longer available. Please remove it and attach the file again.');
      }

      try {
        this.uploadedPrescriptions = this.attachedPrescriptions
          .map((attachedPrescription) => ({
            ...attachedPrescription,
            source: attachedPrescription.uploadid ? 'smd' : 'custom',
          }));
        const uploadCustomPrescriptionsRequests = customPrescriptions
          .map(({file}) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('filename', file.name);
            formData.append('prescriptiontype', 'medicine');

            return FilesService.uploadPrescription(formData);
          });
        const responses = await Promise.all(uploadCustomPrescriptionsRequests);

        /**
         * to keep the order of attached prescriptions,
         * this will loop on `this.uploadedPrescriptions` then each response it will append
         * the response uploadid to the first `this.uploadedPrescriptions` without uploadid
         * this methods assumes that the no. of of prescription without upload in `this.uploadedPrescriptions`
         * is equal to the length of `responses`
         */
        responses.forEach((response) => {
          const matchedUploadedPrescriptionIndex = this.uploadedPrescriptions.findIndex(({uploadid}) => !uploadid);
          this.uploadedPrescriptions[matchedUploadedPrescriptionIndex].uploadid = response.data.uploadid;
        });

        return Promise.resolve(responses);
      } catch (error) {
        return Promise.reject('Uploading prescriptions failed.');
      }
    },
    async uploadIds() {
      if (this.attachedIds.some((file) => !isUploadableFile(file))) {
        return Promise.reject('One of your attached IDs is no longer available. Please remove it and attach the file again.');
      }

      try {
        const uploadIdsRequests = this.attachedIds
          .map((file) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('filename', file.name);
            formData.append('filetype', file.type);

            return FilesService.uploadId(formData);
          });
        const responses = await Promise.all(uploadIdsRequests);
        this.uploadedIds = responses.map((response) => response.data.uploadid);

        return Promise.resolve(responses);
      } catch (error) {
        return Promise.reject('Uploading IDs failed.');
      }
    },
    async postSubmissionFunction({data}) {
      if (data && data.result === 1) {
        this.setMedicineState({
          loading: true,
        });
        this.resetPharmacyOrder();

        // refresh orders list
        try {
          const {data: {orders}} = await MedicineService.getOrders();
          this.setMedicineState({
            orders,
            loading: false,
          });
          this.$router.push({
            name: Routes.MEDICINE_ORDER,
            params: {
              [Routes.MEDICINE_ORDER_ID_PARAM]: data.orderid,
            },
          });
        } catch (error) {
          this.setMedicineState({loading: false});
        }
      } else {
        this.showToast({
          text: 'This service is currently unavailable. Please try again later.',
          theme: 'danger',
        });
      }
    },
  },
}
